
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/AuthContext';
import SignupForm from '@/components/SignupForm';
import { signUpUser, signInUser } from '@/services/authService';
import { redirectToChatApp } from '@/utils/redirectUtils';
import { createOrUpdateUserProfile, updateOrderWithUserEmail, verifyAndEnsureProfile } from '@/services/profileService';
import { supabase } from '@/integrations/supabase/client';
import { fetchOrderDetails } from '@/services/orderService';

// Create a flag to track if profile verification has been attempted
const profileVerificationAttempted = new Set<string>();

const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [initialProcessingDone, setInitialProcessingDone] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [planId, setPlanId] = useState('');
  const { signup } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();

  // Process URL parameters and check order details, similar to ThankYouPage
  useEffect(() => {
    const processUrlParams = async () => {
      // Only run this effect once
      if (initialProcessingDone) return;
      
      try {
        const searchParams = new URLSearchParams(location.search);
        const emailParam = searchParams.get('email');
        const orderIdParam = searchParams.get('order_id');
        const planParam = searchParams.get('plan');
        const creditsParam = searchParams.get('credits');
        
        // Get stored values from session storage as fallbacks
        const storedOrderId = sessionStorage.getItem('last_order_id');
        const storedPlan = sessionStorage.getItem('purchased_plan');
        const storedEmail = sessionStorage.getItem('customer_email');
        
        console.log('Signup details from URL:', { emailParam, orderIdParam, planParam, creditsParam });
        console.log('Signup details from session storage:', { storedOrderId, storedPlan, storedEmail });
        
        // Set order ID - prefer URL value over stored value
        const actualOrderId = orderIdParam || storedOrderId;
        setOrderId(actualOrderId || '');
        
        // Set email - prefer URL value over stored value, with proper decoding
        if (emailParam) {
          const decodedEmail = decodeURIComponent(emailParam);
          setEmail(decodedEmail);
          console.log(`Using email from URL: ${decodedEmail}`);
        } else if (storedEmail) {
          setEmail(storedEmail);
          console.log(`Using email from session storage: ${storedEmail}`);
        }
        
        // Set plan - prefer URL value over stored value
        const actualPlanId = planParam || storedPlan;
        if (actualPlanId) {
          setPlanId(actualPlanId);
          console.log(`Using plan ID: ${actualPlanId}`);
        }
        
        // Store order details in session storage for later use
        if (orderIdParam) {
          sessionStorage.setItem('last_order_id', orderIdParam);
        }
        if (planParam) {
          sessionStorage.setItem('purchased_plan', planParam);
        }
        if (creditsParam) {
          sessionStorage.setItem('purchased_credits', creditsParam);
        }
        if (emailParam) {
          sessionStorage.setItem('customer_email', decodeURIComponent(emailParam));
        }
        
        // If we have an order ID, try to fetch the order details to confirm payment
        if (actualOrderId && retryCount < 2) {
          console.log('Checking order details for order ID:', actualOrderId);
          
          try {
            const orderData = await fetchOrderDetails(actualOrderId);
            
            if (orderData) {
              console.log('Successfully fetched order details:', orderData);
              
              // Set email from order data if needed
              if (!email && orderData.user_email) {
                setEmail(orderData.user_email);
                sessionStorage.setItem('customer_email', orderData.user_email);
                console.log(`Setting email to "${orderData.user_email}" from order data`);
              }
              
              // Check payment status if available
              if (orderData.status === 'PAID') {
                console.log('Order is already paid');
                
                // Initial payment status check
                try {
                  console.log('Confirming payment status with backend function');
                  await supabase.functions.invoke('create-payment-order', {
                    body: {
                      checkPaymentStatus: true,
                      orderId: actualOrderId,
                      fromSignupPage: true
                    }
                  });
                } catch (err) {
                  console.error('Error checking payment status:', err);
                }
                
                toast({
                  title: "Payment confirmed",
                  description: "Your payment has been processed successfully. Complete your account setup.",
                });
              }
            } else if (retryCount < 1) {
              // One more retry after a delay if we still don't have the data
              setTimeout(() => {
                setRetryCount(prev => prev + 1);
              }, 2000);
              return; // Exit early to prevent setting initialProcessingDone
            }
          } catch (err) {
            console.error('Error fetching order details:', err);
          }
        }
        
        // Pre-fill message if email is from URL
        if (emailParam) {
          toast({
            title: "Welcome!",
            description: "Your email has been pre-filled. Please create a password to complete your account setup.",
          });
        }
        
        setInitialProcessingDone(true);
      } catch (error) {
        console.error("Error processing URL parameters:", error);
        setInitialProcessingDone(true);
      }
    };
    
    processUrlParams();
  }, [location.search, retryCount, initialProcessingDone, email, toast]);

  const handleSubmit = async (formData: { 
    name: string; 
    email: string; 
    password: string; 
    confirmPassword: string; 
    agreed: boolean 
  }) => {
    setIsLoading(true);

    try {
      console.log('Creating account with details:', { 
        email: formData.email, 
        name: formData.name,
        orderId,
        planId
      });
      
      // Store data in session storage for consistency with ThankYou page
      if (planId) {
        sessionStorage.setItem('purchased_plan', planId);
      } else if (!sessionStorage.getItem('purchased_plan')) {
        sessionStorage.setItem('purchased_plan', 'basic');
      }
      
      if (!sessionStorage.getItem('purchased_credits')) {
        sessionStorage.setItem('purchased_credits', '1000');
      }
      
      // First sign up the user with Supabase Auth
      const { data, error } = await signUpUser(formData.email, formData.password, { name: formData.name });
      
      if (error) {
        let errorMessage = "There was an error creating your account.";
        
        if (error.message?.includes("already registered")) {
          errorMessage = "This email is already registered. Please try logging in instead.";
        }
        
        toast({
          title: "Signup failed",
          description: errorMessage,
          variant: "destructive",
        });
        
        throw error;
      }
      
      console.log("Signup successful:", data);
      
      // If there's an order ID, update the order record with user email
      if (orderId) {
        try {
          const updateSuccess = await updateOrderWithUserEmail(orderId, formData.email);
          console.log(`Order update ${updateSuccess ? 'successful' : 'failed'} for order ${orderId}`);
        } catch (orderUpdateError) {
          console.error("Error updating order:", orderUpdateError);
          // Don't fail the signup process if order update fails
        }
      }
      
      // Explicit login after signup for consistency with ThankYou page
      console.log("Performing explicit login after signup");
      const { data: loginData, error: loginError } = await signInUser(formData.email, formData.password);
      
      if (loginError) {
        console.error("Login after signup failed:", loginError);
      } else {
        console.log("Login after signup successful:", loginData);
        
        // Additional profile verification - only do once per email
        if (!profileVerificationAttempted.has(formData.email)) {
          profileVerificationAttempted.add(formData.email);
          
          try {
            const profileVerified = await verifyAndEnsureProfile(
              formData.email,
              formData.name,
              planId || sessionStorage.getItem('purchased_plan') || undefined,
              sessionStorage.getItem('purchased_credits') ? Number(sessionStorage.getItem('purchased_credits')) : undefined
            );
            
            console.log("Profile verification result:", profileVerified);
          } catch (profileError) {
            console.error("Error verifying profile:", profileError);
          }
        } else {
          console.log(`Skipping profile verification - already attempted for ${formData.email}`);
        }
      }
      
      toast({
        title: "Account created!",
        description: "Your account has been successfully created.",
      });
      
      // Check if user is coming from purchase flow
      const sourceParam = new URLSearchParams(location.search).get('source');
      const hasOrderInfo = orderId || planId || sessionStorage.getItem('purchased_credits');
      
      if (sourceParam === 'email' || hasOrderInfo) {
        toast({
          title: "Redirecting to Krix Chat...",
          description: "You'll be automatically logged in to the chat application.",
        });
        
        setIsRedirecting(true);
        
        // Add longer delay before redirecting (consistent with ThankYou page)
        setTimeout(() => {
          redirectToChatApp(formData.email, formData.password);
        }, 1500);
      } else {
        // Wait a bit before redirecting to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error: any) {
      let errorMessage = "There was an error creating your account.";
      
      if (error.message?.includes("already registered")) {
        errorMessage = "This email is already registered. Please try logging in instead.";
      }
      
      toast({
        title: "Signup failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-krix-purple/5 to-transparent p-4">
      <SignupForm
        email={email}
        isEmailReadOnly={location.search.includes('email')}
        isLoading={isLoading}
        isRedirecting={isRedirecting}
        showSubscriptionText={Boolean(orderId) || Boolean(planId)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default SignupPage;
